<template>
  <div class="salon-app salon-welcome">
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.welcome.title") }}</h1>
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
      <div class="salon-progress">
        <div class="salon-progress-track">
          <div class="salon-progress-fill" :style="{ width: progressWidth }"></div>
        </div>
        <div class="salon-progress-meta">
          <span class="salon-progress-label">{{ $t("salon.flow.stepOf", { step, total: totalSteps }) }}</span>
        </div>
      </div>
    </header>

    <main class="salon-main">
      <div class="salon-avatar" aria-hidden="true">
        <span v-if="initials">{{ initials }}</span>
        <svg v-else viewBox="0 0 24 24" width="32" height="32">
          <path d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 2c-4 0-7 2-7 4.5V20h14v-1.5c0-2.5-3-4.5-7-4.5z"
            fill="currentColor" />
        </svg>
      </div>

      <div class="salon-greeting">
        <h2 class="salon-greeting-title">{{ greeting }}</h2>
        <p v-if="phoneText" class="salon-greeting-subtitle">{{ $t("salon.welcome.bookingWith", { phone: phoneText }) }}</p>
      </div>

      <!-- Rows are dropped rather than blanked: a member whose very first visit this is
           has no history to show, and an empty "Last Visit —" row reads like a bug. -->
      <div v-if="hasAnyDetail" class="salon-card">
        <div v-if="lastVisitText" class="salon-card-row">
          <span class="salon-card-label">{{ $t("salon.welcome.lastVisit") }}</span>
          <span class="salon-card-value">{{ lastVisitText }}</span>
        </div>

        <div v-if="stylistName" class="salon-card-row">
          <span class="salon-card-label">{{ $t("salon.welcome.usualStylist") }}</span>
          <span class="salon-card-value">{{ stylistName }}</span>
        </div>

        <div v-if="tierName" class="salon-card-row salon-card-row--inline">
          <span class="salon-card-label">{{ $t("salon.welcome.memberTier") }}</span>
          <span class="salon-tier">
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.4l6.1-.8L12 3z"
                fill="currentColor" />
            </svg>
            {{ tierName }}
          </span>
        </div>
      </div>

      <button class="salon-switch" type="button" @click="$emit('switch-number')">
        {{ $t("salon.welcome.switchNumber") }}
      </button>
    </main>

    <nav class="salon-cta">
      <button class="salon-cta-btn" type="button" :disabled="isLoading" @click="$emit('continue')">
        <span>{{ $t("salon.welcome.continueBooking") }}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M5 12h14m-6-6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </nav>
  </div>
</template>

<script>
import MemberAuthService from "@/services/memberAuth.service";
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import "@/salon/styles/tokens.css";

// SH002's second half (Salon_UI/welcome_back) — the returning customer's confirmation
// that they signed in as the right person before the booking proper starts.
const STEP = 3;
const TOTAL_STEPS = 9;

export default {
  name: "SalonWelcomeBack",
  components: {
    SalonProfileMenu,
  },
  props: {
    // SH016 — the avatar in the top bar, and who it stands for.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
    // Login already has the number the customer typed. The API deliberately does not
    // return it: minty_members.phone_number is encrypted and MOO registration has
    // historically written it in plaintext, so decrypting it server-side throws.
    phoneNumber: {
      type: String,
      default: "",
    },
    phoneCode: {
      type: String,
      default: "65",
    },
    // What member/login-otp already returned, so the greeting can render immediately
    // instead of waiting on the summary call.
    user: {
      type: Object,
      default: null,
    },
  },
  emits: ["back", "continue", "switch-number", "profile"],
  data() {
    return {
      step: STEP,
      totalSteps: TOTAL_STEPS,
      isLoading: false,
      summary: null,
    };
  },
  computed: {
    progressWidth() {
      return ((STEP / TOTAL_STEPS) * 100).toFixed(2) + "%";
    },
    member() {
      return (this.summary && this.summary.member) || null;
    },
    firstName() {
      if (this.member && this.member.first_name) {
        return this.member.first_name;
      }
      return (this.user && this.user.name) || "";
    },
    greeting() {
      return this.firstName
        ? this.$t("salon.welcome.greeting", { name: this.firstName })
        : this.$t("salon.welcome.title");
    },
    initials() {
      if (this.member && this.member.initials) {
        return this.member.initials;
      }
      return this.firstName ? this.firstName.slice(0, 2).toUpperCase() : "";
    },
    phoneText() {
      if (!this.phoneNumber) {
        return "";
      }
      const digits = this.phoneNumber.replace(/\D/g, "");
      const grouped = digits.length > 4 ? digits.slice(0, 4) + " " + digits.slice(4) : digits;
      return "+" + this.phoneCode + " " + grouped;
    },
    lastVisitText() {
      return (this.summary && this.summary.last_visit && this.summary.last_visit.text) || "";
    },
    stylistName() {
      return (this.summary && this.summary.usual_stylist && this.summary.usual_stylist.name) || "";
    },
    tierName() {
      return (this.summary && this.summary.member_tier && this.summary.member_tier.name) || "";
    },
    hasAnyDetail() {
      return !!(this.lastVisitText || this.stylistName || this.tierName);
    },
  },
  created() {
    this.getSummary();
  },
  methods: {
    async getSummary() {
      this.isLoading = true;

      try {
        const response = await MemberAuthService.getSalonProfileSummary();

        if (response.data.result.isSuccess) {
          this.summary = response.data.data;
        }
      } catch (error) {
        // The card is a courtesy, not a gate — a failed summary leaves the greeting and
        // Continue booking working rather than blocking the booking on it.
        this.summary = null;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-welcome {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
  border-bottom: 2px solid var(--salon-surface-variant);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  background: transparent;
  color: var(--salon-primary);
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-progress {
  margin-top: 16px;
}

.salon-progress-track {
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: var(--salon-surface-container-highest);
  border-radius: 999px;
}

.salon-progress-fill {
  height: 100%;
  background: var(--salon-primary-container);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.salon-progress-meta {
  margin-top: 8px;
  text-align: center;
}

.salon-progress-label {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--salon-stack-lg) var(--salon-container-padding) 140px;
}

.salon-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin-bottom: var(--salon-stack-lg);
  font-family: var(--salon-font-headline);
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--salon-primary);
  background: #f0ebe3;
  border: 2px solid var(--salon-secondary);
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(28, 28, 30, 0.06);
}

.salon-greeting {
  margin-bottom: var(--salon-stack-lg);
  text-align: center;
}

.salon-greeting-title {
  margin: 0 0 4px;
  font-family: var(--salon-font-headline);
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  letter-spacing: -0.01em;
  color: var(--salon-on-background);
}

.salon-greeting-subtitle {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Detail card ───────────────────────────────────────────────── */
.salon-card {
  width: 100%;
  overflow: hidden;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-surface-variant);
  border-radius: var(--salon-radius-card);
  box-shadow: var(--salon-shadow-soft);
}

.salon-card-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border-bottom: 1px solid var(--salon-surface-variant);
}

.salon-card-row:last-child {
  border-bottom: 0;
}

.salon-card-row--inline {
  flex-direction: row;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
}

.salon-card-label {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.06em;
  color: var(--salon-on-surface-variant);
  text-transform: uppercase;
}

.salon-card-value {
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-tier {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
  padding: 4px 12px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-secondary);
  background: #fdf5d3;
  border: 1px solid rgba(236, 194, 70, 0.3);
  border-radius: 999px;
}

/* ── Switch number ─────────────────────────────────────────────── */
.salon-switch {
  margin-top: var(--salon-stack-md);
  padding: 8px;
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
  text-decoration: underline;
  text-underline-offset: 4px;
  background: none;
  border: 0;
  cursor: pointer;
  transition: color 0.2s ease;
}

.salon-switch:hover {
  color: var(--salon-on-surface);
}

/* ── Bottom CTA ────────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  z-index: 50;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-cta-btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
